import { cn } from "cn";
import { BwChevronRightLine } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { PlaceTypeIcon } from "@/components/ui/status/place-type-icon";
import { renderPlaceAddress } from "@/features/my/_libs/utils";
import { useMyPlaceContext } from "@/features/my/_providers/my-place-context-provider";
import type { PlaceType } from "@/types/openapi-enum";

export type PlaceSwitcherButtonProps = {
  onClick?: () => void;
  className?: string;
  /**
   * `minimal` — icon + address only (staging / owner onboarding header).
   * `default` — pill with border + shadow (rest of member app).
   */
  variant?: "default" | "minimal";
};

export function PlaceSwitcherButton({
  onClick,
  className,
  variant = "default",
}: PlaceSwitcherButtonProps) {
  const { place, places } = useMyPlaceContext();
  const enabled = places.length > 1;

  return (
    <button
      type="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      className={cn(
        /* sorted */
        "flex",
        "items-center gap-x-3",
        "text-text-dark-high text-sm leading-6 font-medium whitespace-nowrap",
        "transition-colors",
        variant === "default" &&
          "bg-bg-light border border-border-dark-low shadow-xs rounded-xl py-2 px-3",
        variant === "minimal" &&
          "py-0 px-0 gap-3 border-0 shadow-none bg-transparent rounded-none",
        enabled ? "cursor-pointer" : "cursor-default",
        variant === "default" && enabled && "hover:bg-bg-dark-min",
        variant === "minimal" && enabled && "hover:opacity-90",
        className,
      )}
      data-test="onboarding-place-switcher-button"
    >
      {place ? (
        <PlaceTypeIcon
          type={place.type as PlaceType}
          size={24}
          className="text-text-primary shrink-0"
          aria-hidden="true"
        />
      ) : (
        <Skeleton className="size-6 rounded shrink-0" />
      )}
      {place ? (
        <span className="text-sm font-medium text-ellipsis overflow-hidden whitespace-nowrap text-text-dark-high flex-1 text-left">
          {renderPlaceAddress(place)}
        </span>
      ) : (
        <Skeleton className="h-4 flex-1 max-w-[60%]" />
      )}
      {place && enabled && (
        <BwChevronRightLine className="ml-auto size-2.5 text-text-dark-high shrink-0" />
      )}
    </button>
  );
}
